import {
  FC,
  useCallback,
  useMemo
} from 'react';
import { nodesForExclusion } from '@app/const';
import {
  Autocomplete,
  FormField,
  Input,
  Switch
} from '@app/form_components';
import { UseObjectFormObject, useObjectForm } from '@app/hooks';
import { ModuleInputsForm } from '@app/page-components/app/canvas/ModuleInputsForm';
import { ModuleOutputsForm } from '@app/page-components/app/canvas/ModuleOutputsForm';
import {
  DesignClass,
  DesignModule,
  DesignObject,
  ModuleInput,
  ModuleVariableReference,
  Option,
  ValueOrVariableType,
  VariableReference,
  WithFillerObjectConfig
} from '@app/types';
import { createUuid } from '@app/utilities';
import { NodeFooter } from './NodeFooter';
import { NodeHeader } from './NodeHeader';

type ModuleOrObjectOption = (
  Option<string>
  & {
    item: DesignObject | DesignModule;
  }
);

export type WithFillerNodeModelFormProps = {
  object: UseObjectFormObject<WithFillerObjectConfig>;
};

export const WithFillerNodeModelForm: FC<WithFillerNodeModelFormProps> = ({
  object
}) => {
  const labels = window.l.property;
  const { createSubmitHandler, getControlProps } = useObjectForm({ object });

  const { modules, objects } = window.apm.serialized.components;
  const options = useMemo(() => {
    return [...modules, ...objects].filter(({ _class, name }) => {
      return name && !nodesForExclusion.has(_class);
    }).sort((a, b) => {
      return a.name!.localeCompare(b.name!);
    });
  }, [modules, objects]);

  const inputsField = getControlProps('inputs', { syncOn: 'change' });
  const outputsField = getControlProps('outputs', { syncOn: 'change' });
  const referenceIdField = getControlProps('referenceId');
  const referenceNodeTypeField = getControlProps('referenceNodeType');
  const referenceObject = options.find(({ id }) => id === referenceIdField.value);

  const handleReferenceChange = (
    id: string | null,
    option: ModuleOrObjectOption | null
  ) => {
    const { _class } = option?.item ?? {};
    referenceIdField.onChange(id ?? '');
    referenceNodeTypeField.onChange(_class);
    if (_class !== DesignClass.module) {
      inputsField.onChange(undefined);
      outputsField.onChange(undefined);
      return;
    }

    const appModule = option?.item as DesignModule;
    inputsField.onChange((appModule.props.config.inputs ?? [])?.map((input): ModuleInput => {
      const base = {
        argumentName: input.argumentName as ModuleVariableReference,
        id: createUuid()
      };
      return (input.type === ValueOrVariableType.value) ? {
        ...base,
        type: input.type,
        value: input.value ?? null
      } : {
        ...base,
        defaultValue: input.defaultValue ?? null,
        sensitiveData: input.sensitiveData ?? false,
        type: input.type,
        varName: input.varName as VariableReference
      };
    }));
    outputsField.onChange([]);
  };

  const getOptions = useCallback((search: string): ModuleOrObjectOption[] => {
    const filtered = (!search) ? options.filter(({ _class }) => {
      return _class === DesignClass.module;
    }) : options.filter(({ id, name }) => {
      return name?.toLowerCase().includes(search.toLowerCase()) || id.toString().includes(search);
    });

    return filtered.slice(0, 30).map((component): ModuleOrObjectOption => {
      return {
        icon: component.props.iconClass,
        item: component,
        label: component.name!,
        value: component.id
      };
    });
  }, [options]);

  return (
    <form
      key="form"
      className="flex-column flex-gap"
      onSubmit={createSubmitHandler()}
    >
      <NodeHeader getControlProps={getControlProps} />
      <Autocomplete<string, ModuleOrObjectOption>
        {...referenceIdField}
        getOptions={getOptions}
        icon="search"
        initSearchValue={referenceObject?.name ?? ''}
        label="Linked Module or Object"
        name="referenceId"
        onChange={handleReferenceChange}
        placeholder="Search for a Module or Object"
      />
      <Input
        {...getControlProps('referenceNodeTimeout')}
        label="Timeout in Seconds"
        placeholder="Maximum time to wait for the linked node to respond"
      />
      {(referenceObject?._class !== DesignClass.module) ? null : (
        <>
          <hr />
          <FormField label="Module Inputs">
            <ModuleInputsForm
              {...inputsField}
              moduleId={referenceObject.id}
            />
          </FormField>
          <FormField label={labels.outputs} tooltip={labels.outputsTooltip}>
            <ModuleOutputsForm
              {...outputsField}
              moduleId={referenceObject.id}
            />
          </FormField>
          <hr />
        </>
      )}
      <Switch
        {...getControlProps('isVerbalFiller')}
        label="Add Verbal Filler"
        tooltip="When enabled typing filler and verbal filler will be played"
      />
      <NodeFooter getControlProps={getControlProps} />
    </form>
  );
};
